"use client";

import React from "react";

// Named icon set — keys are stored in the config, values are inline SVGs.
// Keep in sync with BADGE_ICON_OPTIONS in CRM BadgesEditor.tsx.
export const BADGE_ICON_SVGS: Record<string, React.ReactNode> = {
  shield: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12 3l7 3v5c0 4.5-3 8-7 9.5C8 19 5 15.5 5 11V6l7-3z" /><path d="M9 12l2 2 4-4" /></svg>,
  truck: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M3 6h11v9H3z" /><path d="M14 9h4l3 3v3h-7z" /><circle cx="7" cy="18" r="1.6" /><circle cx="17" cy="18" r="1.6" /></svg>,
  refresh: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M3 9a9 9 0 0 1 15-3l3 3" /><path d="M21 4v5h-5" /><path d="M21 15a9 9 0 0 1-15 3l-3-3" /><path d="M3 20v-5h5" /></svg>,
  clock: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></svg>,
  tag: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" /><circle cx="7" cy="7" r="1" /></svg>,
  star: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87L18.18 21 12 17.77 5.82 21 7 14.14 2 9.27l6.91-1.01z" /></svg>,
  gift: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="20 12 20 22 4 22 4 12" /><rect x="2" y="7" width="20" height="5" /><line x1="12" y1="22" x2="12" y2="7" /><path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z" /><path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z" /></svg>,
  heart: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" /></svg>,
  lock: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></svg>,
  phone: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 13.1a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.6 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L7.91 9.91a16 16 0 0 0 6.18 6.18l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" /></svg>,
  check: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10" /><path d="M9 12l2 2 4-4" /></svg>,
  zap: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" /></svg>,
};

interface TrustStripProps {
  minCartValue?: number | null;
  city?: string;
  badges?: { title: string; sub: string; icon?: string }[];
}

interface TrustItem {
  title: string;
  sub: string;
  icon: React.ReactNode;
}

export const TrustStrip: React.FC<TrustStripProps> = ({ minCartValue, city, badges }) => {
  const defaultItems: TrustItem[] = [
    {
      title: "Verified by GrabLocal",
      sub: "Trusted local seller",
      icon: (
        <svg
          width="22"
          height="22"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M12 3l7 3v5c0 4.5-3 8-7 9.5C8 19 5 15.5 5 11V6l7-3z" />
          <path d="M9 12l2 2 4-4" />
        </svg>
      ),
    },
    {
      title: "Free delivery",
      sub:
        minCartValue != null
          ? `On orders over ₹${minCartValue.toLocaleString("en-IN")}`
          : "Same-day, local",
      icon: (
        <svg
          width="22"
          height="22"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M3 6h11v9H3z" />
          <path d="M14 9h4l3 3v3h-7z" />
          <circle cx="7" cy="18" r="1.6" />
          <circle cx="17" cy="18" r="1.6" />
        </svg>
      ),
    },
    {
      title: "Easy 7-day returns",
      sub: "No-fuss refunds",
      icon: (
        <svg
          width="22"
          height="22"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M3 9a9 9 0 0 1 15-3l3 3" />
          <path d="M21 4v5h-5" />
          <path d="M21 15a9 9 0 0 1-15 3l-3-3" />
          <path d="M3 20v-5h5" />
        </svg>
      ),
    },
    {
      title: "Same-day delivery",
      sub: `In ${city || "your city"}`,
      icon: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <circle cx="12" cy="12" r="9" />
          <path d="M12 7v5l3 2" />
        </svg>
      ),
    },
    // Fallback icons for badge slots 5–6 (custom badges beyond the defaults)
    {
      title: "",
      sub: "",
      icon: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" /><circle cx="7" cy="7" r="1" />
        </svg>
      ),
    },
    {
      title: "",
      sub: "",
      icon: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M12 2l3.09 6.26L22 9.27l-5 4.87L18.18 21 12 17.77 5.82 21 7 14.14 2 9.27l6.91-1.01z" />
        </svg>
      ),
    },
  ];

  const items: TrustItem[] =
    badges && badges.length > 0
      ? badges
          .filter(b => b.title || b.sub)
          .map((badge, i) => ({
            title: badge.title,
            sub: badge.sub,
            icon: (badge.icon && BADGE_ICON_SVGS[badge.icon]) ?? defaultItems[i]?.icon ?? defaultItems[0].icon,
          }))
      : defaultItems.filter(item => item.title || item.sub);

  return (
    <section className="mt-[clamp(28px,3.5vw,40px)]">
      <div
        className="relative overflow-hidden"
        style={{
          background: "var(--navy)",
          color: "#fff",
          borderRadius: "var(--radius-lg)",
          padding: "clamp(18px,2.5vw,28px)",
          boxShadow: "var(--shadow-soft)",
        }}
      >
        <ul
          className="grid grid-cols-2 lg:grid-cols-4 gap-x-4 gap-y-5 lg:gap-x-0"
          style={{ margin: 0, padding: 0, listStyle: "none" }}
        >
          {items.map((item, i) => (
            <li
              key={i}
              className="flex items-center gap-3 lg:px-[clamp(14px,1.6vw,22px)] relative"
            >
              {/* Divider between items on desktop */}
              {i > 0 && (
                <span
                  aria-hidden="true"
                  className="hidden lg:block absolute left-0 top-1/2 -translate-y-1/2"
                  style={{
                    width: 1,
                    height: "60%",
                    background: "rgba(255,255,255,0.14)",
                  }}
                />
              )}

              <span
                aria-hidden="true"
                className="flex items-center justify-center shrink-0"
                style={{
                  width: 44,
                  height: 44,
                  borderRadius: 12,
                  background: "rgba(255,255,255,0.08)",
                  color: "var(--coral)",
                }}
              >
                {item.icon}
              </span>

              <span className="flex flex-col min-w-0">
                <span
                  className="font-semibold leading-tight"
                  style={{ color: "#fff", fontSize: "clamp(13.5px,1.4vw,15px)" }}
                >
                  {item.title}
                </span>
                <span
                  className="leading-snug"
                  style={{
                    color: "rgba(255,255,255,0.6)",
                    fontSize: "clamp(11.5px,1.2vw,12.5px)",
                  }}
                >
                  {item.sub}
                </span>
              </span>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
};
